import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { getClassroomObjectivesReport, getClassroomAssignmentsReport, getClassroomObjectivesMatrix } from '../lib/api';
import { toast } from 'sonner';
import { 
  ArrowLeft, Target, FileText, Users, Trophy,
  ChevronDown, ChevronUp, AlertTriangle, CheckCircle, Download, Table, Eye
} from 'lucide-react';

const ClassReports = () => {
  const navigate = useNavigate();
  const { classroomId } = useParams();
  const [activeTab, setActiveTab] = useState('matrix');
  const [objectivesData, setObjectivesData] = useState(null);
  const [assignmentsData, setAssignmentsData] = useState(null);
  const [matrixData, setMatrixData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedAssignments, setExpandedAssignments] = useState({});

  useEffect(() => {
    const fetchReports = async () => {
      setLoading(true);
      try {
        const [objRes, assignRes, matrixRes] = await Promise.all([
          getClassroomObjectivesReport(classroomId),
          getClassroomAssignmentsReport(classroomId),
          getClassroomObjectivesMatrix(classroomId)
        ]);
        setObjectivesData(objRes.data);
        setAssignmentsData(assignRes.data);
        setMatrixData(matrixRes.data);
      } catch (error) {
        console.error('Failed to fetch reports:', error);
        toast.error('Failed to load reports');
      } finally {
        setLoading(false);
      }
    };

    fetchReports();
  }, [classroomId]);

  const toggleAssignment = (exerciseId) => {
    setExpandedAssignments(prev => ({
      ...prev,
      [exerciseId]: !prev[exerciseId]
    }));
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-neon-green';
    if (score >= 60) return 'text-neon-yellow';
    return 'text-neon-red';
  };

  const getScoreBg = (score) => {
    if (score >= 80) return 'bg-neon-green/20';
    if (score >= 60) return 'bg-neon-yellow/20';
    return 'bg-neon-red/20';
  };

  // Export to CSV
  const exportToCSV = () => {
    if (!assignmentsData?.assignments) {
      toast.error('No data to export');
      return;
    }

    const rows = [['Assignment', 'Date Assigned', 'Objective', 'Student', 'Typing Accuracy', 'Typing Completion', 'Quiz Score', 'Final Score', 'Status']];
    
    assignmentsData.assignments.forEach(assignment => {
      const dateAssigned = assignment.created_at 
        ? new Date(assignment.created_at).toLocaleDateString('en-US')
        : 'N/A';
      
      if (assignment.student_scores && assignment.student_scores.length > 0) {
        assignment.student_scores.forEach(student => {
          rows.push([
            assignment.title,
            dateAssigned,
            assignment.objective_code,
            student.student_name,
            `${student.accuracy || 0}%`,
            `${student.completion || 0}%`,
            `${student.quiz_score || 0}%`,
            `${student.final_score || 0}%`,
            student.status || 'Not Started'
          ]);
        });
      } else {
        rows.push([
          assignment.title,
          dateAssigned,
          assignment.objective_code,
          'No submissions',
          '-', '-', '-', '-', '-'
        ]);
      }
    });

    const csvContent = rows.map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `${objectivesData?.classroom?.name || 'class'}_report_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    toast.success('Report exported to CSV');
  };

  // Export Objectives to CSV
  const exportObjectivesToCSV = () => {
    if (!objectivesData?.objectives) {
      toast.error('No objectives data to export');
      return;
    }

    const rows = [['Objective Code', 'Description', 'Students Tested', 'Avg Accuracy', 'Avg Quiz Score', 'Times Practiced']];
    
    objectivesData.objectives.forEach(obj => {
      rows.push([
        obj.code,
        obj.description?.substring(0, 100) || '',
        obj.unique_students || 0,
        `${obj.avg_accuracy || 0}%`,
        `${obj.avg_quiz_score || 0}%`,
        obj.times_practiced || 0
      ]);
    });

    const csvContent = rows.map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `${objectivesData?.classroom?.name || 'class'}_objectives_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    toast.success('Objectives report exported to CSV');
  };

  // Export Matrix to CSV (Student x Objective format)
  const exportMatrixToCSV = () => {
    if (!matrixData?.matrix || !matrixData?.objectives || matrixData.matrix.length === 0) {
      toast.error('No matrix data to export');
      return;
    }

    // Header row: Student, Objective1, Objective2, ...
    const headers = ['Student', ...matrixData.objectives];
    const rows = [headers];
    
    matrixData.matrix.forEach(row => {
      const csvRow = [row.Student];
      matrixData.objectives.forEach(obj => {
        csvRow.push(row[obj] !== '' ? row[obj] : '');
      });
      rows.push(csvRow);
    });

    const csvContent = rows.map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `${matrixData?.classroom_name || 'class'}_objectives_matrix_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    toast.success('Objectives matrix exported to CSV');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-text-secondary">Loading reports...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-white/5 bg-background-paper/50 backdrop-blur-md sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => navigate(`/classroom/${classroomId}`)}
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="font-heading text-xl font-bold">Class Reports</h1>
              <p className="text-text-secondary text-sm">{objectivesData?.classroom?.name || 'Classroom'}</p>
            </div>
          </div>
          <Button 
            variant="outline" 
            onClick={activeTab === 'matrix' ? exportMatrixToCSV : activeTab === 'objectives' ? exportObjectivesToCSV : exportToCSV}
            className="border-primary text-primary hover:bg-primary hover:text-black"
            data-testid="export-csv-btn"
          >
            <Download className="w-4 h-4 mr-2" />
            Export CSV
          </Button>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-6 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-3 mb-8">
            <TabsTrigger value="matrix" className="flex items-center gap-2" data-testid="matrix-tab">
              <Table className="w-4 h-4" />
              Student × Objective
            </TabsTrigger>
            <TabsTrigger value="objectives" className="flex items-center gap-2" data-testid="objectives-tab">
              <Target className="w-4 h-4" />
              Objectives Summary
            </TabsTrigger>
            <TabsTrigger value="assignments" className="flex items-center gap-2" data-testid="assignments-tab">
              <FileText className="w-4 h-4" />
              Assignment Scores
            </TabsTrigger>
          </TabsList>

          {/* Student × Objective Matrix Tab */}
          <TabsContent value="matrix">
            <Card className="card-glass p-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="font-heading text-lg font-bold">Running Averages by Objective</h2>
                  <p className="text-text-secondary text-sm">Quiz scores averaged per student per objective</p>
                </div>
              </div>
              
              {matrixData?.matrix?.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-white/10">
                        <th className="text-left py-3 px-3 font-medium text-text-secondary sticky left-0 bg-background-paper z-10">
                          Student
                        </th>
                        {matrixData.objectives.map(obj => (
                          <th key={obj} className="text-center py-3 px-2 font-mono text-primary text-xs min-w-[80px]">
                            {obj}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {matrixData.matrix.map((row, idx) => (
                        <tr key={idx} className="border-b border-white/5 hover:bg-white/5">
                          <td className="py-3 px-3 font-medium sticky left-0 bg-background-paper z-10">
                            {row.Student}
                          </td>
                          {matrixData.objectives.map(obj => {
                            const value = row[obj];
                            return (
                              <td key={obj} className="text-center py-3 px-2">
                                {value !== '' && value !== undefined ? (
                                  <span className={`inline-block px-2 py-1 rounded text-xs font-medium ${getScoreBg(value)} ${getScoreColor(value)}`}>
                                    {value}%
                                  </span>
                                ) : (
                                  <span className="text-text-muted">—</span>
                                )}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-12 text-text-muted">
                  <Table className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>No quiz data available yet</p>
                  <p className="text-sm mt-1">Students need to complete exercises with quizzes</p>
                </div>
              )}
              
              {/* Legend */}
              <div className="mt-6 pt-4 border-t border-white/10 flex items-center gap-4 text-xs">
                <span className="text-text-muted">Score Key:</span>
                <span className="flex items-center gap-1">
                  <span className="w-3 h-3 rounded bg-neon-green/20"></span>
                  <span className="text-neon-green">80%+</span>
                </span>
                <span className="flex items-center gap-1">
                  <span className="w-3 h-3 rounded bg-neon-yellow/20"></span>
                  <span className="text-neon-yellow">60-79%</span>
                </span>
                <span className="flex items-center gap-1">
                  <span className="w-3 h-3 rounded bg-neon-red/20"></span>
                  <span className="text-neon-red">&lt;60%</span>
                </span>
                <span className="flex items-center gap-1">
                  <span className="text-text-muted">—</span>
                  <span className="text-text-muted">Not tested</span>
                </span>
              </div>
            </Card>
          </TabsContent>

          {/* Objectives Performance Tab */}
          <TabsContent value="objectives">
            {/* Summary Cards */}
            <div className="grid grid-cols-3 gap-4 mb-8">
              <Card className="card-glass p-4 text-center">
                <p className="text-text-muted text-sm">Objectives Tested</p>
                <p className="font-heading text-3xl font-bold text-primary">
                  {objectivesData?.summary?.total_objectives || 0}
                </p>
              </Card>
              <Card className="card-glass p-4 text-center">
                <p className="text-text-muted text-sm">Total Exercises</p>
                <p className="font-heading text-3xl font-bold text-secondary">
                  {objectivesData?.summary?.total_exercises || 0}
                </p>
              </Card>
              <Card className="card-glass p-4 text-center">
                <p className="text-text-muted text-sm">Completed Sessions</p>
                <p className="font-heading text-3xl font-bold text-neon-green">
                  {objectivesData?.summary?.total_sessions || 0}
                </p>
              </Card>
            </div>

            {/* Objectives List */}
            {objectivesData?.objectives?.length === 0 ? (
              <Card className="card-glass p-8 text-center">
                <Target className="w-12 h-12 text-text-muted mx-auto mb-4" />
                <p className="text-text-secondary">No objectives data yet</p>
                <p className="text-text-muted text-sm">Create assignments to see performance by objective</p>
              </Card>
            ) : (
              <div className="space-y-4">
                {objectivesData?.objectives?.map((obj) => (
                  <Card key={obj.objective_code} className="card-glass p-6" data-testid={`objective-${obj.objective_code}`}>
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <span className="px-3 py-1 bg-primary/20 text-primary rounded-full text-sm font-mono font-bold">
                            {obj.objective_code}
                          </span>
                          <span className="text-text-muted text-sm">
                            {obj.total_sessions} completed sessions
                          </span>
                        </div>
                        <p className="text-text-secondary text-sm">{obj.objective_description}</p>
                      </div>
                      <div className={`px-4 py-2 rounded-lg ${getScoreBg(obj.avg_final_score)}`}>
                        <p className="text-text-muted text-xs text-center">Avg Final</p>
                        <p className={`font-heading text-2xl font-bold ${getScoreColor(obj.avg_final_score)}`}>
                          {obj.avg_final_score}%
                        </p>
                      </div>
                    </div>

                    {/* Performance Metrics */}
                    <div className="grid grid-cols-4 gap-4 mb-4">
                      <div className="text-center p-3 bg-black/20 rounded-lg">
                        <p className="text-text-muted text-xs">Accuracy</p>
                        <p className={`font-mono font-bold ${getScoreColor(obj.avg_accuracy)}`}>
                          {obj.avg_accuracy}%
                        </p>
                      </div>
                      <div className="text-center p-3 bg-black/20 rounded-lg">
                        <p className="text-text-muted text-xs">Completion</p>
                        <p className={`font-mono font-bold ${getScoreColor(obj.avg_completion)}`}>
                          {obj.avg_completion}%
                        </p>
                      </div>
                      <div className="text-center p-3 bg-black/20 rounded-lg">
                        <p className="text-text-muted text-xs">Quiz Score</p>
                        <p className={`font-mono font-bold ${getScoreColor(obj.avg_quiz_score)}`}>
                          {obj.avg_quiz_score}%
                        </p>
                      </div>
                      <div className="text-center p-3 bg-black/20 rounded-lg">
                        <p className="text-text-muted text-xs">Exercises</p>
                        <p className="font-mono font-bold text-secondary">
                          {obj.exercises?.length || 0}
                        </p>
                      </div>
                    </div>

                    {/* Progress Bar */}
                    <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                      <div 
                        className={`h-full ${obj.avg_final_score >= 80 ? 'bg-neon-green' : obj.avg_final_score >= 60 ? 'bg-neon-yellow' : 'bg-neon-red'}`}
                        style={{ width: `${obj.avg_final_score}%` }}
                      />
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Assignment Scores Tab */}
          <TabsContent value="assignments">
            {/* Summary Cards */}
            <div className="grid grid-cols-3 gap-4 mb-8">
              <Card className="card-glass p-4 text-center">
                <p className="text-text-muted text-sm">Total Assignments</p>
                <p className="font-heading text-3xl font-bold text-primary">
                  {assignmentsData?.summary?.total_assignments || 0}
                </p>
              </Card>
              <Card className="card-glass p-4 text-center">
                <p className="text-text-muted text-sm">Total Students</p>
                <p className="font-heading text-3xl font-bold text-secondary">
                  {assignmentsData?.summary?.total_students || 0}
                </p>
              </Card>
              <Card className="card-glass p-4 text-center">
                <p className="text-text-muted text-sm">Class Avg Final Score</p>
                <p className={`font-heading text-3xl font-bold ${getScoreColor(assignmentsData?.summary?.class_avg_final_score || 0)}`}>
                  {assignmentsData?.summary?.class_avg_final_score || 0}%
                </p>
              </Card>
            </div>

            {/* Student Overview */}
            {assignmentsData?.students?.length > 0 && (
              <Card className="card-glass p-6 mb-8">
                <h3 className="font-heading text-lg font-bold mb-4 flex items-center gap-2">
                  <Users className="w-5 h-5 text-primary" />
                  Student Overview
                </h3>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-white/10">
                        <th className="text-left py-3 px-4 text-text-muted text-sm font-medium">Student</th>
                        <th className="text-center py-3 px-4 text-text-muted text-sm font-medium">Completed</th>
                        <th className="text-center py-3 px-4 text-text-muted text-sm font-medium">Avg Final Score</th>
                      </tr>
                    </thead>
                    <tbody>
                      {assignmentsData.students.map((student) => (
                        <tr key={student.student_id} className="border-b border-white/5 hover:bg-white/5">
                          <td className="py-3 px-4">
                            <p className="font-medium">{student.student_name}</p>
                            <p className="text-text-muted text-xs">{student.student_email}</p>
                          </td>
                          <td className="text-center py-3 px-4">
                            <span className={`px-2 py-1 rounded text-sm ${
                              student.assignments_completed === student.assignments_total
                                ? 'bg-neon-green/20 text-neon-green'
                                : 'bg-neon-yellow/20 text-neon-yellow'
                            }`}>
                              {student.assignments_completed} / {student.assignments_total}
                            </span>
                          </td>
                          <td className="text-center py-3 px-4">
                            <span className={`font-mono font-bold text-lg ${getScoreColor(student.avg_final_score)}`}>
                              {student.avg_final_score}%
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            )}

            {/* Assignments List */}
            {assignmentsData?.assignments?.length === 0 ? (
              <Card className="card-glass p-8 text-center">
                <FileText className="w-12 h-12 text-text-muted mx-auto mb-4" />
                <p className="text-text-secondary">No assignments yet</p>
                <p className="text-text-muted text-sm">Create assignments to see scores</p>
              </Card>
            ) : (
              <div className="space-y-4">
                <h3 className="font-heading text-lg font-bold flex items-center gap-2">
                  <Trophy className="w-5 h-5 text-neon-yellow" />
                  Assignment Scores
                </h3>
                {assignmentsData?.assignments?.map((assignment) => (
                  <Card key={assignment.exercise_id} className="card-glass overflow-hidden" data-testid={`assignment-${assignment.exercise_id}`}>
                    {/* Assignment Header - Clickable */}
                    <div 
                      className="p-4 cursor-pointer hover:bg-white/5 transition-colors"
                      onClick={() => toggleAssignment(assignment.exercise_id)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-1">
                            <h4 className="font-heading font-bold">{assignment.title}</h4>
                            <span className="px-2 py-0.5 bg-primary/20 text-primary rounded text-xs font-mono">
                              {assignment.objective_code}
                            </span>
                          </div>
                          <p className="text-text-muted text-sm">
                            {assignment.completed_count} / {assignment.total_students} students completed
                            {assignment.created_at && (
                              <span className="ml-3 text-text-secondary">
                                • Assigned: {new Date(assignment.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                              </span>
                            )}
                          </p>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className={`px-4 py-2 rounded-lg ${getScoreBg(assignment.avg_final_score)}`}>
                            <p className="text-text-muted text-xs text-center">Class Avg</p>
                            <p className={`font-heading text-xl font-bold ${getScoreColor(assignment.avg_final_score)}`}>
                              {assignment.avg_final_score}%
                            </p>
                          </div>
                          {expandedAssignments[assignment.exercise_id] ? (
                            <ChevronUp className="w-5 h-5 text-text-muted" />
                          ) : (
                            <ChevronDown className="w-5 h-5 text-text-muted" />
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Expanded Student Scores */}
                    {expandedAssignments[assignment.exercise_id] && (
                      <div className="border-t border-white/10 p-4 bg-black/20">
                        <table className="w-full">
                          <thead>
                            <tr className="border-b border-white/10">
                              <th className="text-left py-2 px-3 text-text-muted text-xs font-medium">Student</th>
                              <th className="text-center py-2 px-3 text-text-muted text-xs font-medium">Status</th>
                              <th className="text-center py-2 px-3 text-text-muted text-xs font-medium">Accuracy</th>
                              <th className="text-center py-2 px-3 text-text-muted text-xs font-medium">Completion</th>
                              <th className="text-center py-2 px-3 text-text-muted text-xs font-medium">Quiz</th>
                              <th className="text-center py-2 px-3 text-text-muted text-xs font-medium">Final Score</th>
                            </tr>
                          </thead>
                          <tbody>
                            {assignment.student_scores.map((score) => {
                              // Calculate final score if not present (backwards compatibility)
                              const accuracy = score.accuracy || 0;
                              const completion = score.completion || 0;
                              const quizScore = score.quiz_score || 0;
                              const isEmptySubmission = score.empty_submission || (accuracy === 0 && completion === 0);
                              const pasteSuspected = score.paste_suspected || (completion >= 80 && accuracy < 20);
                              const finalScore = score.final_score || 
                                ((accuracy > 0 || completion > 0 || quizScore > 0) 
                                  ? Math.round((accuracy + completion + quizScore) / 3 * 10) / 10 
                                  : 0);
                              
                              return (
                              <tr key={score.student_id} className={`border-b ${isEmptySubmission ? 'border-neon-red/30 bg-neon-red/5' : pasteSuspected ? 'border-neon-orange/30 bg-neon-orange/5' : 'border-white/5'}`}>
                                <td className="py-2 px-3">
                                  <div className="flex items-center gap-2">
                                    {score.student_name}
                                    {isEmptySubmission && (
                                      <span className="px-1.5 py-0.5 bg-neon-red/20 text-neon-red text-xs rounded">
                                        No Work
                                      </span>
                                    )}
                                    {pasteSuspected && !isEmptySubmission && (
                                      <span className="px-1.5 py-0.5 bg-neon-orange/20 text-neon-orange text-xs rounded">
                                        ⚠️ Paste
                                      </span>
                                    )}
                                    {score.wpm_flagged && !isEmptySubmission && !pasteSuspected && (
                                      <AlertTriangle className="w-4 h-4 text-neon-red" title="Suspicious WPM" />
                                    )}
                                    {score.tab_switches > 0 && (
                                      <span 
                                        className={`px-1.5 py-0.5 text-xs rounded flex items-center gap-1 ${
                                          score.tab_switch_flagged 
                                            ? 'bg-neon-red/20 text-neon-red' 
                                            : 'bg-status-warning/20 text-status-warning'
                                        }`}
                                        title={`Left page ${score.tab_switches} time${score.tab_switches > 1 ? 's' : ''} during quiz`}
                                      >
                                        <Eye className="w-3 h-3" />
                                        {score.tab_switches}
                                      </span>
                                    )}
                                  </div>
                                </td>
                                <td className="text-center py-2 px-3">
                                  {score.status === 'completed' ? (
                                    <CheckCircle className="w-4 h-4 text-neon-green mx-auto" />
                                  ) : (
                                    <span className="text-text-muted text-xs">{score.status}</span>
                                  )}
                                </td>
                                <td className={`text-center py-2 px-3 font-mono ${getScoreColor(accuracy)}`}>
                                  {accuracy}%
                                </td>
                                <td className={`text-center py-2 px-3 font-mono ${getScoreColor(completion)}`}>
                                  {completion}%
                                </td>
                                <td className={`text-center py-2 px-3 font-mono ${getScoreColor(quizScore)}`}>
                                  {quizScore}%
                                </td>
                                <td className={`text-center py-2 px-3 font-mono font-bold ${getScoreColor(finalScore)}`}>
                                  {finalScore}%
                                </td>
                              </tr>
                            )})}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default ClassReports;
